import httpClient from 'lib/httpClient';
import s3Client from 'lib/s3Client';

const PRESIGNED_URL_ENDPOINT = 'uploads/presigned-url';
const MAX_BATCH_SIZE = 10;

const getPresignedUrls = (files) => httpClient.post(PRESIGNED_URL_ENDPOINT, { files });

const buildFileData = (presignedFile, originalFile) => ({
  s3_key: presignedFile.s3_key,
  name: originalFile.name,
  content_type: originalFile.type,
});

const uploadBatch = async (batch) => {
  const result = {};

  const filesPayload = batch.map(([, fileInfo]) => ({
    filename: fileInfo.name,
    content_type: fileInfo.content_type,
  }));

  const presignedResponse = await getPresignedUrls(filesPayload);
  const presignedFiles = presignedResponse.data?.files || presignedResponse.files;

  if (!Array.isArray(presignedFiles) || presignedFiles.length < batch.length) {
    throw new Error('Server returned an invalid upload URL. Please try again.');
  }

  const uploadPromises = batch.map(([questionName, fileInfo], index) => {
    const presignedFile = presignedFiles[index];

    if (!presignedFile?.upload_url) {
      return Promise.reject(new Error('Server returned an invalid upload URL. Please try again.'));
    }

    return s3Client
      .put(presignedFile.upload_url, fileInfo.blob, {
        headers: { 'Content-Type': fileInfo.content_type },
      })
      .then(() => {
        result[questionName] = {
          s3_key: presignedFile.s3_key,
          name: fileInfo.name,
          content_type: fileInfo.content_type,
        };
      });
  });

  const results = await Promise.allSettled(uploadPromises);
  const failed = results.filter((r) => r.status === 'rejected');
  if (failed.length > 0) {
    throw new Error(`Failed to upload ${failed.length} file(s). Please try again.`);
  }

  return result;
};

const syncOfflineFiles = async (pendingFiles) => {
  const entries = Object.entries(pendingFiles);
  if (entries.length === 0) return {};

  const batches = [];
  for (let i = 0; i < entries.length; i += MAX_BATCH_SIZE) {
    batches.push(entries.slice(i, i + MAX_BATCH_SIZE));
  }

  const batchResults = await batches.reduce(
    (chain, batch) => chain.then(async (acc) => ({ ...acc, ...(await uploadBatch(batch)) })),
    Promise.resolve({}),
  );

  return batchResults;
};

const uploadService = {
  getPresignedUrls,
  buildFileData,
  syncOfflineFiles,
};

export default uploadService;
